import React, { createContext, useContext, useReducer, useEffect } from "react";
import { signOut as amplifySignOut } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import { AuthService } from "../services/auth.service";
import { config } from "../pages/Test/config";
import { postAppRecord } from "../pages/Test/DataFetcher";

const AuthContext = createContext();

// 初始狀態
const initialState = {
  user: null,
  clinicName: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

// Action 類型
const AUTH_ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_USER: "SET_USER",
  SET_CLINIC: "SET_CLINIC",
  SET_AUTHENTICATED: "SET_AUTHENTICATED",
  SET_ERROR: "SET_ERROR",
  CLEAR_USER: "CLEAR_USER",
  UPDATE_USER: "UPDATE_USER",
};

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload,
        isLoading: false,
      };
    case AUTH_ACTIONS.SET_CLINIC:
      return { ...state, clinicName: action.payload };
    case AUTH_ACTIONS.SET_AUTHENTICATED:
      return { ...state, isAuthenticated: action.payload };
    case AUTH_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
    case AUTH_ACTIONS.CLEAR_USER:
      return {
        ...state,
        user: null,
        clinicName: null,
        isAuthenticated: false,
      };
    case AUTH_ACTIONS.UPDATE_USER:
      return { ...state, user: { ...state.user, ...action.payload } };
    default:
      return state;
  }
}

// Provider 組件
export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // 初始化認證狀態
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

        // 使用混合認證檢查，不依賴 localStorage
        const isAuthenticated = await AuthService.isAuthenticatedHybrid();
        if (isAuthenticated) {
          // 從 JWT Token 獲取用戶信息
          console.log("用戶已通過 JWT Token 認證");

          // 嘗試從 Cognito 獲取當前用戶信息
          try {
            const { getCurrentUser, fetchUserAttributes } = await import(
              "aws-amplify/auth"
            );
            const currentUser = await getCurrentUser();
            const attributes = await fetchUserAttributes();

            const determineClinic = (username, attrs) => {
              if (username === "admin") return "all";
              const c = attrs["custom:clinic"];
              if (c && ["hansheng", "yoyo", "all"].includes(c)) return c;
              if (username.toLowerCase().includes("hansheng")) return "hansheng";
              if (username.toLowerCase().includes("yoyo")) return "yoyo";
              return "hansheng";
            };

            const userData = {
              account: currentUser.username,
              email: attributes.email || "",
              clinic: determineClinic(currentUser.username, attributes),
              createTime: config.getUTC8Time().toISOString(),
              cognitoUser: currentUser,
              isLocalAuth: false,
            };

            console.log("從 Cognito 獲取到用戶數據:", userData);
            dispatch({ type: AUTH_ACTIONS.SET_USER, payload: userData });
            dispatch({
              type: AUTH_ACTIONS.SET_CLINIC,
              payload: userData.clinic,
            });
          } catch (userError) {
            console.log("無法從 Cognito 獲取用戶信息:", userError);
            // 如果無法獲取用戶信息，至少設置認證狀態
            dispatch({ type: AUTH_ACTIONS.SET_AUTHENTICATED, payload: true });
          }
        }
      } catch (error) {
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      } finally {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    };

    initializeAuth();
  }, []);

  // 定期檢查 token 有效性
  useEffect(() => {
    if (!state.user) return;

    const tokenCheckInterval = setInterval(async () => {
      try {
        const needsReauth = await AuthService.needsReauthentication();
        if (needsReauth) {
          dispatch({ type: AUTH_ACTIONS.CLEAR_USER });
        }
      } catch (error) {
        console.log("定期 token 檢查失敗:", error);
      }
    }, 5 * 60 * 1000); // 5分鐘

    return () => clearInterval(tokenCheckInterval);
  }, [state.user]);

  // 登入
  const signIn = async (userData) => {
    try {
      if (!userData.createTime) {
        userData.createTime = config.getUTC8Time().toISOString();
      }

      dispatch({ type: AUTH_ACTIONS.SET_USER, payload: userData });
      dispatch({ type: AUTH_ACTIONS.SET_CLINIC, payload: userData.clinic });

      // 記錄登入活動（所有環境都發送）
      const isProduction = window.location.href.includes("master");
      const mode = isProduction ? "master" : "dvp";

      // 獲取用戶 IP 地址和地理位置
      let userIP = "unknown";
      let location = "unknown";
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        userIP = data.ip;

        // 獲取地理位置信息
        try {
          const geoResponse = await fetch(`https://ipapi.co/${userIP}/json/`);
          const geoData = await geoResponse.json();
          if (geoData.city && geoData.country_name) {
            location = `${geoData.city}, ${geoData.country_name}`;
          } else if (geoData.country_name) {
            location = geoData.country_name;
          }
        } catch (geoError) {
          console.error("獲取地理位置失敗:", geoError);
        }
      } catch (ipError) {
        console.error("獲取 IP 地址失敗:", ipError);
      }

      try {
        await postAppRecord({
          clinic: "all",
          user: userData.account || "unknown",
          status: "signIn",
          mode: mode,
          ip: userIP,
          location: location,
        });
        console.log(
          `${mode} 環境登入記錄已發送到 app_record，IP: ${userIP}，位置: ${location}`
        );
      } catch (recordError) {
        console.error("發送登入記錄失敗:", recordError);
      }
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
    }
  };

  // 登出
  const signOut = async () => {
    try {
      // 記錄登出活動（所有環境都發送，在清除用戶狀態之前）
      if (state.user) {
        const isProduction = window.location.href.includes("master");
        const mode = isProduction ? "master" : "dvp";

        // 獲取用戶 IP 地址和地理位置
        let userIP = "unknown";
        let location = "unknown";
        try {
          const response = await fetch("https://api.ipify.org?format=json");
          const data = await response.json();
          userIP = data.ip;

          // 獲取地理位置信息
          try {
            const geoResponse = await fetch(`https://ipapi.co/${userIP}/json/`);
            const geoData = await geoResponse.json();
            if (geoData.city && geoData.country_name) {
              location = `${geoData.city}, ${geoData.country_name}`;
            } else if (geoData.country_name) {
              location = geoData.country_name;
            }
          } catch (geoError) {
            console.error("獲取地理位置失敗:", geoError);
          }
        } catch (ipError) {
          console.error("獲取 IP 地址失敗:", ipError);
        }

        try {
          await postAppRecord({
            clinic: "all",
            user: state.user.account || "unknown",
            status: "signOut",
            mode: mode,
            ip: userIP,
            location: location,
          });
          console.log(
            `${mode} 環境登出記錄已發送到 app_record，IP: ${userIP}，位置: ${location}`
          );
        } catch (recordError) {
          console.error("發送登出記錄失敗:", recordError);
        }
      }

      // 登出 Cognito 用戶
      await amplifySignOut();
    } catch (error) {
      console.error("登出錯誤:", error);
    }

    dispatch({ type: AUTH_ACTIONS.CLEAR_USER });
  };

  // 更新用戶信息
  const updateUser = (updates) => {
    if (state.user) {
      dispatch({ type: AUTH_ACTIONS.UPDATE_USER, payload: updates });
    }
  };

  // 手動設置測試用戶（用於調試）
  const setTestUser = (testUserData) => {
    dispatch({ type: AUTH_ACTIONS.SET_USER, payload: testUserData });
    dispatch({ type: AUTH_ACTIONS.SET_CLINIC, payload: testUserData.clinic });
  };

  // 清除用戶狀態。同時登出 Cognito，避免 app 狀態已清但瀏覽器還留著 session，
  // 之後再登入會遇到 "There is already a signed in user"
  const clearUserState = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_USER });
    amplifySignOut().catch((error) => {
      console.error("清除狀態時登出 Cognito 失敗:", error);
    });
  };

  const value = {
    ...state,
    signIn,
    signOut,
    updateUser,
    setTestUser,
    clearUserState,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
